import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';

/**
 * PremiumGate — shown in place of a locked page when the user's plan
 * doesn't include the requested feature.
 *
 * Props:
 *   featureName   {string}  Display name, e.g. "AI Marketing"
 *   featureIcon   {string}  Lucide icon name, e.g. "Megaphone"
 *   description   {string}  Short description of what the feature does
 *   requiredPlan  {string}  Human-readable plan name that unlocks this feature
 *   planId        {string}  Internal plan ID to pre-select on billing page
 */
const PremiumGate = ({
  featureName = 'Premium Feature',
  featureIcon = 'Lock',
  description = 'Upgrade your plan to unlock this feature.',
  requiredPlan = 'Full Suite',
  planId = 'crm_voice_website_marketing',
}) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate(`/billing?highlight=${planId}`);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center min-h-[60vh] px-6 py-16">
      {/* Icon badge */}
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center">
          <Icon name={featureIcon} size={36} className="text-primary" />
        </div>
        <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center shadow-md">
          <Icon name="Lock" size={14} className="text-white" />
        </div>
      </div>
      {/* Heading */}
      <h2 className="text-2xl font-bold text-foreground mb-2 text-center">
        {featureName} is a Premium Feature
      </h2>
      <p className="text-muted-foreground text-center max-w-md mb-8 leading-relaxed">
        {description}
      </p>
      {/* Required plan badge */}
      <div className="flex items-center space-x-2 mb-8 px-4 py-2 bg-primary/5 border border-primary/20 rounded-full">
        <Icon name="Sparkles" size={14} className="text-primary" />
        <span className="text-sm font-medium text-primary">
          Available on <strong>{requiredPlan}</strong> and above
        </span>
      </div>
      {/* CTA buttons */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button
          onClick={handleUpgrade}
          className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-colors shadow-sm"
        >
          <Icon name="Zap" size={16} />
          <span>Upgrade Now</span>
        </button>
        <button
          onClick={() => navigate('/billing')}
          className="flex items-center space-x-2 px-6 py-3 bg-background border border-border text-foreground rounded-xl font-medium hover:bg-muted/50 transition-colors"
        >
          <Icon name="CreditCard" size={16} />
          <span>View All Plans</span>
        </button>
      </div>
      {/* Feature highlights */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-lg w-full">
        {[
          { icon: 'Shield', text: 'Cancel anytime' },
          { icon: 'Zap', text: 'Instant activation' },
          { icon: 'HeadphonesIcon', text: '24/7 support' },
        ]?.map((item) => (
          <div
            key={item?.text}
            className="flex items-center space-x-2 text-sm text-muted-foreground justify-center"
          >
            <Icon name={item?.icon} size={14} className="text-primary/60 flex-shrink-0" />
            <span>{item?.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PremiumGate;
